/**
 * "A newer build is live" — one flag, two ways to raise it.
 *
 * 1. The poll in `startVersionWatch` notices the served entry bundle changed.
 * 2. A lazy chunk import fails, which is what a redeployed tab feels from the
 *    inside: the file it asks for by hash no longer exists.
 *
 * Both mean the same thing and have the same cure, so they set the same flag
 * and the banner is rendered once, globally.
 */
import { useEffect } from "react";
import { create } from "zustand";
import { startVersionWatch } from "@/lib/appVersion";

interface VersionState {
  stale: boolean;
  markStale: () => void;
}

export const useVersionStore = create<VersionState>((set) => ({
  stale: false,
  markStale: () => set({ stale: true }),
}));

/** Non-React callers (i18n bundle loading, api.ts) raise it through this. */
export function markBuildStale(): void {
  useVersionStore.getState().markStale();
}

/** Mount once, at the app root. Starts the poll; returns the current flag. */
export function useNewVersion(): boolean {
  const stale = useVersionStore((s) => s.stale);
  useEffect(() => startVersionWatch({ onStale: markBuildStale }), []);
  return stale;
}
